import React from 'react';
import { FUEL_META } from './constants';
import { Modal, Field, ModalFooter } from './modalComponents';
import { fmtUSD } from './formatters';
import { Truck, Fuel } from 'lucide-react';

const TransactionModal = ({ isOpen, onClose, editingTx, txForm, setTxForm, inventory, aircraft, owners, staff, trucks, onSave, saving }) => {
  if (!isOpen) return null;

  // Sécuriser les tableaux
  const safeAircraft = Array.isArray(aircraft) ? aircraft : [];
  const safeOwners = Array.isArray(owners) ? owners : [];
  const safeStaff = Array.isArray(staff) ? staff : [];
  const safeTrucks = Array.isArray(trucks) ? trucks : [];
  const safeInventory = Array.isArray(inventory) ? inventory : [];

  const setForm = (key, val) => setTxForm(prev => ({ ...prev, [key]: val }));

  const total = txForm.gallons && txForm.price_per_gallon 
    ? parseFloat(txForm.gallons) * parseFloat(txForm.price_per_gallon) 
    : null;

  // Récupérer le truck sélectionné
  const selectedTruck = safeTrucks.find(t => t.id === txForm.truck_id);
  
  // Mettre à jour le fuel type et le prix quand on sélectionne un truck
  const handleTruckChange = (truckId) => {
    if (truckId) {
      const truck = safeTrucks.find(t => t.id === truckId);
      if (truck) {
        setForm('truck_id', truck.id);
        setForm('fuel_type', truck.fuel_type);
        // Option: pré-remplir le prix depuis le truck
        if (truck.current_price) {
          setForm('price_per_gallon', truck.current_price);
        }
      }
    } else {
      setForm('truck_id', '');
      // Remettre le fuel type par défaut depuis l'inventaire
      const defaultTank = safeInventory[0];
      if (defaultTank) {
        setForm('fuel_type', defaultTank.fuel_type);
        setForm('price_per_gallon', defaultTank.price_per_gallon);
      }
    }
  };

  // Mettre à jour le fuel type quand on change manuellement
  const handleFuelTypeChange = (fuelType) => {
    const selected = safeInventory.find(i => i.fuel_type === fuelType);
    setForm('fuel_type', fuelType);
    if (selected?.price_per_gallon) {
      setForm('price_per_gallon', String(selected.price_per_gallon));
    }
    // Si on change le fuel type, désactiver le truck sélectionné
    if (txForm.truck_id) {
      setForm('truck_id', '');
    }
  };

  return (
    <Modal
      title={editingTx
        ? `Edit Transaction · ${editingTx.transaction_number || editingTx.id?.slice(0, 8)}`
        : 'Fuel Aircraft'}
      onClose={onClose}
    >
      <div className="space-y-3">
        {editingTx && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 text-xs text-yellow-300">
            ⚠️ Modifying gallons or fuel type will automatically adjust the tank stock.
          </div>
        )}

        <Field label="Aircraft *">
          <select 
            value={txForm.aircraft_id} 
            onChange={e => setForm('aircraft_id', e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
          >
            <option value="">— Select aircraft —</option>
            {safeAircraft.map(a => (
              <option key={a.id} value={a.id}>{a.tail_number}{a.model ? ` · ${a.model}` : ''}</option>
            ))}
          </select>
        </Field>

        <Field label="Owner">
          <select 
            value={txForm.owner_id} 
            onChange={e => setForm('owner_id', e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
          >
            <option value="">— Select owner —</option>
            {safeOwners.map(o => <option key={o.id} value={o.id}>{o.company_name || o.email}</option>)}
          </select>
        </Field>

        {/* ✅ Sélection de la source : Station ou Truck */}
        <div className="bg-gray-700/30 rounded-lg p-3">
          <label className="block text-gray-400 text-xs mb-2 font-medium">Fuel Source</label>
          <div className="flex gap-4 mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio"
                value="station"
                checked={!txForm.truck_id}
                onChange={() => handleTruckChange('')}
                className="text-blue-500"
              />
              <Fuel size={14} className="text-blue-400" />
              <span className="text-gray-300 text-sm">Station Tank</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input 
                type="radio"
                value="truck"
                checked={!!txForm.truck_id}
                onChange={() => {}}
                className="text-blue-500"
              />
              <Truck size={14} className="text-green-400" />
              <span className="text-gray-300 text-sm">Fuel Truck</span>
            </label>
          </div>

          {/* Si Truck sélectionné, afficher la liste des trucks */}
          {txForm.truck_id !== undefined && (
            <div className="mt-2">
              <select 
                value={txForm.truck_id || ''} 
                onChange={e => handleTruckChange(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
              >
                <option value="">— Select fuel truck —</option>
                {safeTrucks.map(t => (
                  <option key={t.id} value={t.id}>
                    🚛 {t.truck_number} - {t.fuel_type} - {t.current_volume_gallons} gal available
                  </option>
                ))}
              </select>
              {selectedTruck && (
                <div className="mt-2 text-xs text-gray-400">
                  Truck fuel level: {selectedTruck.current_volume_gallons} / {selectedTruck.capacity_gallons} gal
                </div>
              )}
            </div>
          )}
        </div>

        <Field label="Fuel type *">
          <select
            value={txForm.fuel_type}
            onChange={e => handleFuelTypeChange(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
            disabled={!!txForm.truck_id}
          >
            {safeInventory.map(i => (
              <option key={i.fuel_type} value={i.fuel_type}>
                {FUEL_META[i.fuel_type]?.label || i.fuel_type} · {i.current_volume_gallons} gal available
              </option>
            ))}
          </select>
          {txForm.truck_id && (
            <p className="text-xs text-blue-400 mt-1">
              Fuel type is determined by the selected truck
            </p>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Volume (gallons) *">
            <input 
              type="number" 
              min="0" 
              step="0.1" 
              placeholder="e.g. 50"
              value={txForm.gallons} 
              onChange={e => setForm('gallons', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
            />
            {selectedTruck && (
              <p className="text-xs text-gray-500 mt-1">
                Max available in truck: {selectedTruck.current_volume_gallons} gal
              </p>
            )}
          </Field>
          <Field label="Price / gallon *">
            <input 
              type="number" 
              min="0" 
              step="0.0001" 
              placeholder="e.g. 5.85"
              value={txForm.price_per_gallon} 
              onChange={e => setForm('price_per_gallon', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm" 
            />
          </Field>
        </div>

        {total !== null && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-sm">
            <span className="text-gray-400">Estimated total: </span>
            <span className="text-green-400 font-bold">{fmtUSD(total)}</span>
          </div>
        )}

        <Field label="Staff (optional)">
          <select 
            value={txForm.staff_id} 
            onChange={e => setForm('staff_id', e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg p-2 text-white text-sm"
          >
            <option value="">— Self-service / terminal —</option>
            {safeStaff.map(s => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
          </select>
        </Field>
      </div>
      <ModalFooter
        onCancel={onClose}
        onSave={onSave}
        saving={saving}
        disabled={!txForm.aircraft_id || !txForm.gallons || !txForm.price_per_gallon}
        label={editingTx ? 'Save Changes' : 'Save Transaction'}
      />
    </Modal>
  );
};

export default TransactionModal;